// s3Utils.js
// Frontend utilities for file upload via API Gateway (Lambda -> S3)
import { S3Client, ListObjectsV2Command, GetObjectCommand, PutObjectCommand } from '@aws-sdk/client-s3';

// Base URL for your API Gateway, e.g. 
// https://k36u8u0xre.execute-api.us-west-2.amazonaws.com/prod
// Prefer env var, but fall back to hard-coded API URL if missing
const API_URL = process.env.REACT_APP_API_GATEWAY_URL || "https://k36u8u0xre.execute-api.us-west-2.amazonaws.com/prod";

// Debug function to log environment variables (without exposing secrets)
function debugEnvironment() {
  console.log('🔍 Environment Variables Debug:');
  console.log('REACT_APP_AWS_ACCESS_KEY_ID:', process.env.REACT_APP_AWS_ACCESS_KEY_ID ? '✅ Set' : '❌ Missing');
  console.log('REACT_APP_AWS_SECRET_ACCESS_KEY:', process.env.REACT_APP_AWS_SECRET_ACCESS_KEY ? '✅ Set' : '❌ Missing');
  console.log('REACT_APP_AWS_REGION:', process.env.REACT_APP_AWS_REGION || '❌ Missing');
  console.log('REACT_APP_S3_BUCKET:', process.env.REACT_APP_S3_BUCKET || '❌ Missing');
  console.log('REACT_APP_API_GATEWAY_URL:', API_URL);
  
  // Check for common credential issues
  if (!process.env.REACT_APP_AWS_ACCESS_KEY_ID || !process.env.REACT_APP_AWS_SECRET_ACCESS_KEY) {
    console.error('❌ AWS credentials are missing from environment variables');
    return false;
  }
  
  if (!process.env.REACT_APP_S3_BUCKET) {
    console.error('❌ S3 bucket name is missing from environment variables');
    return false;
  }
  
  return true;
}

// S3 Client for direct access (development)
let s3Client = null;

function getS3Client() {
  if (!s3Client) {
    // Debug environment before creating client
    const envValid = debugEnvironment();
    
    if (!envValid) {
      throw new Error('Invalid AWS configuration. Check environment variables.');
    }

    try {
      s3Client = new S3Client({
        region: process.env.REACT_APP_AWS_REGION || 'us-west-2',
        credentials: {
          accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
          secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
        },
      });
      
      console.log('✅ S3 Client created successfully');
    } catch (error) {
      console.error('❌ Failed to create S3 Client:', error);
      throw new Error(`S3 Client creation failed: ${error.message}`);
    }
  }
  return s3Client;
}

/**
 * Create a folder in S3 for a new user
 * @param {string} userId - User ID (used as folder name)
 * @param {string} username - Username for display
 * @returns {Promise<any>} - Response from S3
 */
export async function createUserFolderInS3(userId, username) {
  console.log(`📁 Creating S3 folder for user ${username} (${userId})`);
  
  try {
    // Validate inputs
    if (!userId || !username) {
      throw new Error('User ID and username are required');
    }

    const s3 = getS3Client();
    const bucketName = process.env.REACT_APP_S3_BUCKET;
    
    if (!bucketName) {
      throw new Error('S3 bucket name is not configured');
    }

    // Create an empty object with a key ending in '/' to represent a folder
    const folderKey = `${userId}/`;
    const command = new PutObjectCommand({
      Bucket: bucketName,
      Key: folderKey,
      Body: '', // Empty body for folder marker
      ContentType: 'application/x-directory',
      Metadata: {
        'created-by': 'user-management',
        'user-id': userId,
        'username': username,
        'created-at': new Date().toISOString()
      }
    });

    console.log('🚀 Sending S3 command:', {
      Bucket: bucketName,
      Key: folderKey,
      Region: process.env.REACT_APP_AWS_REGION
    });

    const response = await s3.send(command);
    
    console.log(`✅ Successfully created S3 folder for user ${username} (${userId})`);
    console.log('📋 S3 Response:', response);
    
    return { 
      success: true, 
      folderKey, 
      response,
      bucketName,
      region: process.env.REACT_APP_AWS_REGION
    };
  } catch (error) {
    console.error('❌ Error creating user folder in S3:', error);
    console.error('📊 Error details:', {
      name: error.name,
      message: error.message,
      code: error.Code || error.$metadata?.httpStatusCode,
      requestId: error.RequestId,
      region: process.env.REACT_APP_AWS_REGION,
      bucket: process.env.REACT_APP_S3_BUCKET
    });
    
    // Provide more specific error messages
    let errorMessage = error.message;
    
    if (error.name === 'InvalidAccessKeyId') {
      errorMessage = 'Invalid AWS Access Key ID. Please check your credentials.';
    } else if (error.name === 'SignatureDoesNotMatch') {
      errorMessage = 'AWS Secret Access Key is incorrect. Please check your credentials.';
    } else if (error.name === 'NoSuchBucket') {
      errorMessage = `S3 bucket "${process.env.REACT_APP_S3_BUCKET}" does not exist or you don't have access.`;
    } else if (error.name === 'AccessDenied') {
      errorMessage = 'Access denied. Check your IAM permissions for this S3 bucket.';
    } else if (error.Code === 'NetworkingError') {
      errorMessage = 'Network error. Please check your internet connection.';
    }
    
    throw new Error(errorMessage);
  }
}

/**
 * Check if a user's folder exists in S3
 * @param {string} userId - User ID
 * @returns {Promise<boolean>} - Whether folder exists
 */
export async function checkUserFolderExists(userId) {
  try {
    console.log(`🔍 Checking if folder exists for user ${userId}`);
    
    const s3 = getS3Client();
    const folderKey = `${userId}/`;
    const command = new ListObjectsV2Command({
      Bucket: process.env.REACT_APP_S3_BUCKET,
      Prefix: folderKey,
      MaxKeys: 1
    });
    
    const response = await s3.send(command);
    const exists = response.Contents && response.Contents.length > 0;
    
    console.log(`📁 Folder exists for ${userId}: ${exists}`);
    return exists;
  } catch (error) {
    console.error('❌ Error checking user folder:', error);
    return false;
  }
}

/**
 * Convert a File/Blob to a base64 string that Lambda can decode.
 * @param {File|Blob} file
 * @returns {Promise<string>} base64 string
 */
async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        // Remove data URL prefix if present
        const base64 = result.split(',')[1] || result;
        resolve(base64);
      } else {
        reject(new Error('Failed to convert file to base64'));
      }
    };
    reader.onerror = () => reject(new Error('FileReader error'));
    reader.readAsDataURL(file);
  });
}

/**
 * List all files in a user's S3 folder
 * @param {string} folder - S3 folder/prefix
 * @returns {Promise<Array>} - Array of file objects
 */
export async function listS3Files(folder) {
  console.log(`📋 Listing files in folder: ${folder}`);
  
  try {
    const s3 = getS3Client();
    const bucketName = process.env.REACT_APP_S3_BUCKET;
    
    if (!bucketName) {
      throw new Error('S3 bucket name is not configured');
    }

    const command = new ListObjectsV2Command({
      Bucket: bucketName,
      Prefix: folder + '/',
      MaxKeys: 1000
    });

    console.log('🚀 Sending list command:', {
      Bucket: bucketName,
      Prefix: folder + '/',
      Region: process.env.REACT_APP_AWS_REGION
    });

    const response = await s3.send(command);
    
    console.log(`📊 Found ${response.Contents?.length || 0} objects in S3`);
    
    if (!response.Contents || response.Contents.length === 0) {
      console.log('📂 No files found in folder');
      return [];
    }

    // Convert S3 objects to file format
    const files = response.Contents
      .filter(obj => !obj.Key.endsWith('/')) // Filter out folder markers
      .map(obj => {
        const fileName = obj.Key.split('/').pop();
        const fileExtension = fileName.split('.').pop().toLowerCase();
        const mimeType = getMimeType(fileExtension);
        
        return {
          id: obj.ETag?.replace(/"/g, '') || Date.now() + Math.random(),
          name: fileName,
          originalName: fileName,
          size: obj.Size || 0,
          type: mimeType,
          uploadedAt: obj.LastModified || new Date(),
          url: `https://${bucketName}.s3.${process.env.REACT_APP_AWS_REGION || 'us-west-2'}.amazonaws.com/${obj.Key}`,
          key: obj.Key,
          metadata: {
            title: fileName.split('.')[0],
            description: '',
            tags: [],
            alt: fileName.split('.')[0]
          }
        };
      });

    console.log(`✅ Successfully processed ${files.length} files`);
    return files;
  } catch (error) {
    console.error('❌ Error listing S3 files:', error);
    console.error('📊 Error details:', {
      name: error.name,
      message: error.message,
      code: error.Code || error.$metadata?.httpStatusCode,
      requestId: error.RequestId,
      folder: folder,
      bucket: process.env.REACT_APP_S3_BUCKET,
      region: process.env.REACT_APP_AWS_REGION
    });
    
    // Provide more specific error messages
    let errorMessage = error.message;
    
    if (error.name === 'InvalidAccessKeyId') {
      errorMessage = 'Invalid AWS Access Key ID. Please check your credentials.';
    } else if (error.name === 'SignatureDoesNotMatch') {
      errorMessage = 'AWS Secret Access Key is incorrect. Please check your credentials.';
    } else if (error.name === 'NoSuchBucket') {
      errorMessage = `S3 bucket "${process.env.REACT_APP_S3_BUCKET}" does not exist or you don't have access.`;
    } else if (error.name === 'AccessDenied') {
      errorMessage = 'Access denied. Check your IAM permissions for listing objects in this S3 bucket.';
    } else if (error.Code === 'NetworkingError') {
      errorMessage = 'Network error. Please check your internet connection.';
    }
    
    throw new Error(errorMessage);
  }
}

/**
 * Get file MIME type based on extension
 * @param {string} extension - File extension
 * @returns {string} MIME type
 */
function getMimeType(extension) {
  const mimeTypes = {
    // Images
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'png': 'image/png',
    'gif': 'image/gif',
    'webp': 'image/webp',
    'svg': 'image/svg+xml',
    
    // Videos
    'mp4': 'video/mp4',
    'avi': 'video/x-msvideo',
    'mov': 'video/quicktime',
    'wmv': 'video/x-ms-wmv',
    'flv': 'video/x-flv',
    'webm': 'video/webm',
    
    // Audio
    'mp3': 'audio/mpeg',
    'wav': 'audio/wav',
    'ogg': 'audio/ogg',
    'aac': 'audio/aac',
    'flac': 'audio/flac',
    
    // Documents
    'pdf': 'application/pdf',
    'doc': 'application/msword',
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'xls': 'application/vnd.ms-excel',
    'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'ppt': 'application/vnd.ms-powerpoint',
    'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'txt': 'text/plain',
    'rtf': 'application/rtf',
    
    // Archives
    'zip': 'application/zip',
    'rar': 'application/x-rar-compressed',
    '7z': 'application/x-7z-compressed',
    'tar': 'application/x-tar',
    'gz': 'application/gzip',
    
    // Code
    'js': 'text/javascript',
    'css': 'text/css',
    'html': 'text/html',
    'json': 'application/json',
    'xml': 'application/xml',
    'py': 'text/x-python',
    'java': 'text/x-java-source',
    'cpp': 'text/x-c++src',
    'c': 'text/x-csrc',
    
    // Default
    'default': 'application/octet-stream'
  };
  
  return mimeTypes[extension] || mimeTypes.default;
}

/**
 * Upload a file via API Gateway -> Lambda -> S3.
 *
 * Expects your Lambda to accept a JSON body like:
 * {
 *   "file": "<base64 string>",
 *   "folder": "uploads",
 *   "filename": "example.png"
 * }
 *
 * And respond with JSON like:
 * {
 *   "url": "https://bucket.s3.amazonaws.com/uploads/anonymous/123-file.png"
 * }
 *
 * @param {File} file - File object from an <input type="file">
 * @param {string} folder - S3 folder/prefix (default: "uploads")
 * @returns {Promise<any>} - Parsed JSON response from the API (e.g. {url})
 */
export async function uploadViaApiGateway(file, folder = "uploads") {
  try {
    if (!API_URL) {
      throw new Error(
        "API URL is not configured (REACT_APP_API_GATEWAY_URL is missing)"
      );
    }

    if (!file) {
      throw new Error("No file provided");
    }

    console.log(`📤 Uploading file ${file.name} to folder ${folder} via API Gateway`);

    const base64 = await fileToBase64(file);
    
    const response = await fetch(`${API_URL}/upload`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        file: base64,
        folder,
        filename: file.name,
      }),
    });

    if (!response.ok) {
      const text = await response.text();
      console.error("Upload API error", response.status, text);
      throw new Error(`Upload failed with status ${response.status}: ${text}`);
    }

    const result = await response.json();
    console.log('✅ Upload successful:', result);
    
    // Typically result = {url: "https://..."}
    return result;
  } catch (error) {
    console.error("API Gateway upload error:", error);
    throw error;
  }
}

/**
 * Legacy alias for code that still calls uploadToS3().
 * This now uses API Gateway under the hood instead of direct S3.
 */
export async function uploadToS3(file, folder = "uploads") {
  return uploadViaApiGateway(file, folder);
}

/**
 * Rename a file in S3 (copy to new name, then delete old)
 * @param {string} oldKey - Current S3 key
 * @param {string} newKey - New S3 key
 * @returns {Promise<any>} - Response from the API
 */
export async function renameFileInS3(oldKey, newKey) {
  try {
    if (!API_URL) {
      throw new Error("API URL is not configured for rename operation");
    }

    console.log(`🔄 Renaming file from ${oldKey} to ${newKey}`);

    const response = await fetch(`${API_URL}/rename`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        oldKey,
        newKey,
      }),
    });

    if (!response.ok) {
      const text = await response.text();
      console.error("Rename API error", response.status, text);
      throw new Error(`Rename failed with status ${response.status}: ${text}`);
    }

    const result = await response.json();
    console.log('✅ Rename successful:', result);
    return result;
  } catch (error) {
    console.error("File rename error:", error);
    throw error;
  }
}

/**
 * Update file metadata (this would be stored in a separate database in production)
 * @param {string} fileKey - S3 key of the file
 * @param {object} metadata - New metadata object
 * @returns {Promise<any>} - Response from the API
 */
export async function updateFileMetadata(fileKey, metadata) {
  try {
    if (!API_URL) {
      throw new Error("API URL is not configured for metadata update");
    }

    console.log(`📝 Updating metadata for file ${fileKey}`);

    const response = await fetch(`${API_URL}/metadata`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        fileKey,
        metadata,
      }),
    });

    if (!response.ok) {
      const text = await response.text();
      console.error("Metadata update API error", response.status, text);
      throw new Error(`Metadata update failed with status ${response.status}: ${text}`);
    }

    const result = await response.json();
    console.log('✅ Metadata update successful:', result);
    return result;
  } catch (error) {
    console.error("File metadata update error:", error);
    throw error;
  }
}

/**
 * Stub for deleteFromS3 used by FileList.jsx.
 * For now this just logs and pretends success.
 * Later we can wire this to a real API endpoint (DELETE -> Lambda -> S3).
 */
export async function deleteFromS3(key) {
  console.warn(
    "deleteFromS3 is currently a stub. No real delete is happening for key:",
    key
  );
  
  // Return a shape that FileList is expecting (likely {success: true})
  return { success: true };
}

/**
 * Stub for generatePresignedUrl used by FileList.jsx.
 * For now this returns an empty string.
 * Later we can wire this to an API that returns a real presigned URL.
 */
export async function generatePresignedUrl(key, expiresIn = 3600) {
  console.warn(
    "generatePresignedUrl is currently a stub. No real presigned URL is generated for key:",
    key,
    "expiresIn:",
    expiresIn
  );
  
  // Return an empty string or a placeholder URL.
  return "";
}